import _ from "lodash";
import { convertDate } from "@Services/luxonHelper";
import { GroupTotalFormatters } from "slickgrid-react";
import { formatBytes } from "@Services/constants";
import { t } from "@i18n";
import type { ColumnDefinition, GridRowBase, SlickGrid1Ref, ToolbarItem } from "@CubeComponents/html/SlickGrid1";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

const millisecondsToDays = (milliseconds: number): number => {
  const days = milliseconds / (1000 * 60 * 60 * 24);
  return Math.floor(days);
};

type DateValue = number | string | null | undefined;

interface FilesGridExcelRow {
  nextActionDueDate?: DateValue;
  createdDate?: DateValue;
  reviews?: { createdDate?: DateValue; initiationDate?: DateValue };
}

/**
 * A row of the file-versions grid. This is the SHAPE THE GRID READS, not a server response type:
 * `FilesGrid` builds it by flattening a file version together with the review that version sits in
 * (`reviews`, `currentReviewLevel`, `reviewName`, `serialId`, …), so no single Zod schema covers it.
 *
 * The index signature is required twice over: `ColumnDefinition<T>.field` is `keyof T` and several
 * columns address nested values by dotted path (`"file.space.spaceName"`,
 * `"file.space.project.projectId"`, `"createdBy.fullName"`), and `GridRowBase` is a weak type, so a
 * row declaring no member in common with it would be rejected outright.
 */
export interface FilesGridRow extends GridRowBase {
  [key: string]: unknown;
  fileName?: string;
  fileType?: string;
  size?: number;
  fileVersionNumber?: number;
  revisionSeries?: { label?: string } | null;
  conversionStatus?: string;
  createdDate?: DateValue;
  nextActionDueDate?: DateValue;
  serialId?: string | number | null;
  reviewName?: string;
  reviewFlow?: string;
  indexAtProjectLevel?: number;
  file?: {
    indexAtProjectLevel?: number;
    space?: { spaceName?: string; project?: { projectId?: string } };
  };
  /**
   * Both members are real and both are read: the "Created by" column's `_valueGetter` pulls
   * `createdBy.fullName`, while its formatter hands the whole row to `userAvatar`, which renders
   * from `createdBy._id`. `FilesGrid`'s own row type declares only `_id`, so listing just
   * `fullName` here made the two types share no member and tripped weak-type detection.
   */
  createdBy?: { _id?: ObjectIdString; fullName?: string };
  reviews?: {
    reviewStatus?: string;
    createdDate?: DateValue;
    initiationDate?: number;
    finishedDate?: number;
    status?: { name?: string };
  };
  currentReviewLevel?: ReviewLevelOrSentinel;
}

/**
 * The current review level, or a UI sentinel. When rows are grouped the grid collapses differing
 * levels into the literal string "Varies" (or ""), so this is genuinely a union — `FilesGrid`'s
 * `getReviewStatus` tests for both before reading `.title`.
 */
export type ReviewLevelOrSentinel = string | { title?: string; serial?: number; finishTime?: number } | null;

/** Renders a cell from the whole row (avatar, status chip, level name). */
type FilesGridCellTemplate<TRow extends FilesGridRow> = (row: TRow) => unknown;

/**
 * A column as this helper emits it. `headerText` and `isReviewColumnType` are not part of
 * `ColumnDefinition` — the first is read by the column-picker UI, the second is how `FilesGrid`
 * hides review columns when a file is in no review — so both are declared here rather than widened
 * into the shared grid type.
 */
export type FilesGridColumn<TRow extends FilesGridRow = FilesGridRow> = ColumnDefinition<TRow> & {
  headerText?: string;
  isReviewColumnType?: boolean;
  /** Not on `ColumnDefinition`; consumed by SlickGrid's grouping plugin. */
  groupTotalsFormatter?: unknown;
  /** Not on `ColumnDefinition`; SlickGrid's per-column filter descriptor. */
  filter?: { filterPredicate?: (row: TRow, searchData: { parsedSearchTerms: string[] }) => boolean | undefined };
  grouping?: unknown;
};

/**
 * The grid's live column list, at the type it was mounted with.
 *
 * Read through the PUBLIC `getColumns()` rather than the grid's `columns` field, which
 * @slickgrid-universal declares `protected` (node_modules/@slickgrid-universal/common/dist/types/
 * core/slickGrid.d.ts:35 vs :531 — `getColumns()` returns that very field, dist/esm/core/
 * slickGrid.js:2441). What it hands back is the array `SlickGrid1` was mounted with from its
 * `columnDefinitions` prop, but declared at slickgrid's own erased `Column` type, which drops both
 * the row type and `_valueGetter` — the two members the grouping formatter below reads. Re-typed
 * once here instead of at each of its three read sites.
 */
const gridColumns = <TRow extends FilesGridRow>(grid: SlickGrid1Ref<TRow>): FilesGridColumn<TRow>[] =>
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- slickgrid-react round trip, see doc comment above: the same objects this grid was given, re-typed at the library boundary (SlickGrid1's own `readGridColumn` states the identical assertion)
  grid.slickGrid.getColumns() as unknown as FilesGridColumn<TRow>[];

export const getFilesGridColumns = <TRow extends FilesGridRow = FilesGridRow>({
  userAvatar,
  statusInReviewTemplate,
  showProjectID,
  extraColumns,
  currentReviewLevelFormat,
  getReviewStatus,
  reactGridRef,
}: {
  userAvatar: FilesGridCellTemplate<TRow>;
  statusInReviewTemplate: FilesGridCellTemplate<TRow>;
  showProjectID?: boolean;
  /** Caller-supplied extra columns, merged over the built-ins by id. */
  extraColumns?: Record<string, FilesGridColumn<TRow>>;
  currentReviewLevelFormat: FilesGridCellTemplate<TRow>;
  getReviewStatus: (row: TRow) => unknown;
  reactGridRef: React.RefObject<SlickGrid1Ref<TRow> | null>;
  /**
   * DEAD PROP: `FilesGrid` passes `formatDate`, but this helper never destructures it — the
   * createdDate column formats inline via `convertDate`. Declared so the call site keeps
   * type-checking; removing it from the caller would be a behaviour-neutral cleanup.
   */
  formatDate?: FilesGridCellTemplate<TRow>;
}): FilesGridColumn<TRow>[] => {
  let mapper: Record<string, FilesGridColumn<TRow>> = {
    fileId: {
      field: "fileId",
      id: "fileId",
      headerText: t("File ID"),
      name: t("File ID"),
      width: 100,

      originalWidth: 100,
      textAlign: "Left",
      _valueGetter: (params: TRow) => {
        if (params.file?.space?.project?.projectId) {
          return `${params.file.space.project.projectId}-${params.file.indexAtProjectLevel}`;
        }
        return "";
      },
      filterType: "Text",
    },
    fileName: {
      field: "fileName",
      id: "fileName",
      width: 300,

      originalWidth: 300,
      textAlign: "Left",
      wrapText: true,
      _valueGetter: (params: TRow) => params.fileName,
      headerText: t("File Name"),
      name: t("File Name"),
      filterType: "Text",
    },
    fileType: {
      field: "fileType",
      id: "fileType",
      headerText: t("File Type"),
      name: t("File Type"),

      width: 150,
      _valueGetter: (params: TRow) => params.fileType,
      originalWidth: 150,
      textAlign: "Left",
      filterType: "Text",
    },
    fileSize: {
      field: "size",
      id: "fileSize",
      headerText: t("File Size"),
      name: t("File Size"),
      width: 100,

      originalWidth: 100,
      textAlign: "Left",
      _valueGetter: (params: TRow) => {
        if (params.size) {
          const size = formatBytes(params.size);
          return size;
        }
        return "";
      },
      _filterValueGetter: (params: TRow) => params.size || 0,
      groupTotalsFormatter: GroupTotalFormatters.sumTotals,
      filterType: "FileSize",
    },
    Version: {
      field: "fileVersionNumber",
      id: "Version",
      headerText: t("Version"),
      name: t("Version"),
      width: 100,

      originalWidth: 100,
      textAlign: "Left",
      _valueGetter: (params: TRow) => {
        if (params.fileVersionNumber) {
          return `V${params.fileVersionNumber}`;
        }
        return "";
      },
      filterType: "Text",
    },
    Revision: {
      field: "Revision",
      id: "Revision",
      headerText: t("Revision"),
      name: t("Revision"),
      width: 100,

      originalWidth: 100,
      initiallyHidden: true,
      textAlign: "Left",
      _valueGetter: (params: TRow) => params.revisionSeries?.label || "-",
      filterType: "Text",
    },
    durationAtCurrentLevel: {
      field: "durationAtCurrentLevel",
      id: "durationAtCurrentLevel",
      headerText: t("Duration at Current Level"),
      name: t("Duration at Current Level"),
      width: 175,
      originalWidth: 175,
      textAlign: "Left",

      isReviewColumnType: true,
      initiallyHidden: true,
      _valueGetter: (params: TRow) => {
        const { currentReviewLevel, reviews } = params;
        // `reviews` is dereferenced unguarded. Safe in practice only because the sole caller
        // (`FilesGrid.buildFilesWithReviews`) always sets it to at least `{}`; a second caller that
        // omitted it would throw here. Assertions below are erased.
        if (reviews!.reviewStatus == "Open") {
          if (currentReviewLevel) {
            // `currentReviewLevel` is a real union (see ReviewLevelOrSentinel), so the level object is
            // picked out by `typeof` rather than asserted over the sentinel.
            // KNOWN BUG, preserved verbatim: when rows are grouped it IS the string "Varies", which
            // carries no finish time, so those rows render "0 days".
            const finishTime = typeof currentReviewLevel === "object" ? currentReviewLevel.finishTime : undefined;
            return `${finishTime ? millisecondsToDays(Date.now() - finishTime) : 0} days`;
          }
        }
        return "-";
      },
    },
    totalDuration: {
      field: "totalDuration",
      id: "totalDuration",
      headerText: t("Total Duration"),
      name: t("Total Duration"),
      width: 175,
      originalWidth: 175,
      textAlign: "Left",

      isReviewColumnType: true,
      initiallyHidden: true,
      _valueGetter: (params: TRow) => {
        // Unguarded; see the note on durationAtCurrentLevel. Erased assertion.
        const { reviewStatus, initiationDate, finishedDate } = params.reviews!;
        let duration;
        if (reviewStatus == "Closed") {
          duration = finishedDate! - initiationDate!;
        } else if (reviewStatus == "Revoked") {
          duration = finishedDate! - initiationDate!;
        } else if (reviewStatus == "Open") {
          duration = Date.now() - initiationDate!;
        }
        if (duration) {
          const days = millisecondsToDays(duration);
          return Math.max(days, 0);
        }
        return null;
      },
      formatter: (data: number, params: number, a: unknown, b: unknown, value: TRow) => {
        // Unguarded; see the note on durationAtCurrentLevel. Erased assertion.
        const { reviewStatus, initiationDate, finishedDate } = value.reviews!;
        let duration;
        if (reviewStatus == "Closed") {
          duration = finishedDate! - initiationDate!;
        } else if (reviewStatus == "Revoked") {
          duration = finishedDate! - initiationDate!;
        } else if (reviewStatus == "Open") {
          duration = Date.now() - initiationDate!;
        }
        if (duration) {
          const days = millisecondsToDays(duration);
          return `${Math.max(days, 0)} days`;
        }
        return "-";
      },
      filterType: "Text",
    },
    dueDateOfLevelReview: {
      field: "dueDateOfLevelReview",
      id: "dueDateOfLevelReview",
      headerText: t("Due Date of Current Level"),
      name: t("Due Date of Current Level"),
      width: 175,

      originalWidth: 175,

      textAlign: "Left",
      isReviewColumnType: true,
      initiallyHidden: true,
      _valueGetter: (params: TRow) => {
        if (params.nextActionDueDate === "" || params.nextActionDueDate == null) return null;
        return Number(params.nextActionDueDate);
      },
      formatter: (_row: number, _cell: number, _value: unknown, _columnDef: unknown, dataContext: TRow) => {
        if (dataContext.nextActionDueDate === "" || dataContext.nextActionDueDate == null) return "-";
        return convertDate(Number(dataContext.nextActionDueDate), "dateForTooltip");
      },
      _excelValueGetter: (params: FilesGridExcelRow) =>
        params.nextActionDueDate != null && params.nextActionDueDate !== ""
          ? convertDate(Number(params.nextActionDueDate), "dateForTooltip")
          : "-",
      filterType: "Date",
    },
    reviewCreatedDate: {
      field: "reviewCreatedDate",
      id: "reviewCreatedDate",
      headerText: t("Review Created On"),
      name: t("Review Created On"),
      width: 175,
      originalWidth: 175,

      textAlign: "Left",
      isReviewColumnType: true,
      initiallyHidden: true,
      _valueGetter: (args: TRow) => {
        if (!args.reviews?.createdDate) return null;
        return Number(args.reviews.createdDate);
      },
      formatter: (_row: number, _cell: number, _value: unknown, _columnDef: unknown, dataContext: TRow) => {
        if (!dataContext.reviews?.createdDate) return "";
        return convertDate(Number(dataContext.reviews.createdDate), "dateForTooltip");
      },
      _excelValueGetter: (params: FilesGridExcelRow) =>
        params.reviews?.createdDate ? convertDate(Number(params.reviews.createdDate), "dateForTooltip") : "",
      filterType: "Date",
    },
    reviewInitiatedDate: {
      field: "reviewInitiatedDate",
      id: "reviewInitiatedDate",
      headerText: t("Review Initiated On"),
      name: t("Review Initiated On"),
      width: 175,
      originalWidth: 175,

      textAlign: "Left",
      isReviewColumnType: true,
      initiallyHidden: true,
      _valueGetter: (args: TRow) => {
        if (!args.reviews?.initiationDate) return null;
        return Number(args.reviews.initiationDate);
      },
      formatter: (_row: number, _cell: number, _value: unknown, _columnDef: unknown, dataContext: TRow) => {
        if (!dataContext.reviews?.initiationDate) return "";
        return convertDate(Number(dataContext.reviews.initiationDate), "dateForTooltip");
      },
      _excelValueGetter: (params: FilesGridExcelRow) =>
        params.reviews?.initiationDate ? convertDate(Number(params.reviews.initiationDate), "dateForTooltip") : "",
      filterType: "Date",
    },
    statusInReviews: {
      field: "statusInReviews",
      id: "statusInReviews",
      headerText: t("Status"),
      name: t("Status"),
      width: 170,
      originalWidth: 170,
      textAlign: "Left",
      // `.reviews` unguarded while `.status` IS guarded one step later. Erased assertion.
      _valueGetter: (args: TRow) => args.reviews!.status?.name || "",
      formatter: (data: number, params: number, a: unknown, b: unknown, value: TRow) => statusInReviewTemplate(value),
      filterType: "Text",
    },
    serialId: {
      field: "serialId",
      id: "serialId",
      headerText: t("Review ID"),
      name: t("Review ID"),
      width: 100,

      originalWidth: 100,

      textAlign: "Left",
      _valueGetter: (args: TRow) => {
        const { serialId } = args;
        if (([undefined, null] as unknown[]).includes(serialId)) {
          return "";
        }
        return `RE - ${serialId}`;
      },
      isReviewColumnType: true,
      filterType: "Text",
    },
    reviewName: {
      field: "reviewName",
      id: "reviewName",
      headerText: t("Review Name"),
      name: t("Review Name"),
      width: 150,
      wrapText: true,

      originalWidth: 150,
      textAlign: "Left",
      _valueGetter: (args: TRow) => args.reviewName || "",
      isReviewColumnType: true,
      filterType: "Text",
    },
    reviewFlowName: {
      field: "reviewFlowName",
      id: "reviewFlowName",
      headerText: t("Review Flow"),
      name: t("Review Flow"),
      width: 150,
      wrapText: true,

      originalWidth: 150,
      textAlign: "Left",
      isReviewColumnType: true,
      _valueGetter: (args: TRow) => {
        if (args.__group) {
          return "";
        }
        return args.reviewFlow || "";
      },
      filterType: "Text",
    },
    currentReviewLevelSerial: {
      field: "currentReviewLevelSerial",
      id: "currentReviewLevelSerial",
      headerText: t("Current Level ID"),
      name: t("Current Level ID"),
      width: 150,

      originalWidth: 150,
      textAlign: "Left",
      isReviewColumnType: true,
      _valueGetter: (args: TRow) => {
        const { currentReviewLevel } = args;
        if (currentReviewLevel === undefined || currentReviewLevel === null) {
          return "-";
        }
        // KNOWN BUG, preserved verbatim: the "Varies" sentinel is a string and carries no `serial`,
        // so a grouped row renders "Level undefined".
        const serial = typeof currentReviewLevel === "object" ? currentReviewLevel.serial : undefined;
        return `Level ${serial}`;
      },
      filterType: "Text",
    },
    currentReviewLevelName: {
      field: "currentReviewLevelName",
      id: "currentReviewLevelName",
      headerText: t("Current Level Name"),
      name: t("Current Level Name"),
      width: 175,
      originalWidth: 175,

      textAlign: "Left",
      isReviewColumnType: true,
      formatter: (data: number, params: number, a: unknown, b: unknown, value: TRow) => currentReviewLevelFormat(value),
      _valueGetter: (args: TRow) => getReviewStatus(args),
      filterType: "Text",
    },
    spaceName: {
      field: "file.space.spaceName",
      id: "spaceName",
      headerText: t("Space"),

      name: t("Space"),
      width: 120,
      wrapText: true,
      originalWidth: 120,
      _valueGetter: (args: TRow) => _.get(args, "file.space.spaceName"),
      textAlign: "Left",
      filterType: "Text",
    },
    fullName: {
      field: "createdBy.fullName",
      id: "fullName",
      headerText: t("Created by"),
      name: t("Created by"),
      width: 200,
      originalWidth: 200,
      textAlign: "Left",
      _valueGetter: (args: TRow) => _.get(args, "createdBy.fullName") || "",
      formatter: (data: number, params: number, a: unknown, b: unknown, value: TRow) => userAvatar(value),
      filterType: "Text",
    },
    createdDate: {
      field: "createdDate",
      id: "createdDate",
      headerText: t("Created On"),
      name: t("Created On"),
      _valueGetter: (params: TRow) => {
        if (!params.createdDate) return null;
        return Number(params.createdDate);
      },
      formatter: (_row: number, _cell: number, _value: unknown, _columnDef: unknown, dataContext: TRow) => {
        if (!dataContext.createdDate) return "";
        return convertDate(Number(dataContext.createdDate), "dateForTooltip");
      },
      _excelValueGetter: (params: FilesGridExcelRow) =>
        params.createdDate ? convertDate(Number(params.createdDate), "dateForTooltip") : "",

      width: 200,
      originalWidth: 200,
      textAlign: "Left",
      filterType: "Date",
    },
    conversionStatus: {
      field: "conversionStatus",
      id: "conversionStatus",
      headerText: t("Conversion Status"),
      name: t("Conversion Status"),
      width: 120,
      originalWidth: 120,
      textAlign: "Left",

      initiallyHidden: true,
      _valueGetter: (params: TRow) => {
        const mapper: Record<string, string> = {
          NOT_STARTED: "Not Started",
          RETRY: "Retry",
          IN_PROGRESS: "In Progress",
          COMPLETED: "Completed",
          ERRORED: "Errored",
          NOT_REQUIRED: "",
        };
        if (Object.hasOwn(mapper, params.conversionStatus!)) {
          return mapper[params.conversionStatus!];
        }
        return "";
      },
      filterType: "Text",
    },
  };

  if (showProjectID) {
    mapper.projectId = {
      field: "file.space.project.projectId",
      id: "projectId",
      headerText: t("Project ID"),
      name: t("Project ID"),
      width: 80,

      originalWidth: 80,
      _valueGetter: (args: TRow) => _.get(args, "file.space.project.projectId"),
      textAlign: "Left",
    };
  }
  mapper = { ...mapper, ...extraColumns };

  return Object.values(mapper).map(
    (data: FilesGridColumn<TRow>): FilesGridColumn<TRow> => ({
      filterable: true,
      sortable: true,
      groupable: true,
      type: "string",
      filter: {
        filterPredicate: (columnData: TRow, searchData: { parsedSearchTerms: string[] }) => {
          // `_valueGetter` is declared `=> unknown`, so the value is stringified through `String()`
          // rather than asserted to something carrying a `toString` — same output for every value
          // that gets past the null/undefined probe above.
          if (data._valueGetter && !([undefined, null] as unknown[]).includes(data._valueGetter(columnData))) {
            return String(data._valueGetter(columnData))
              .toLowerCase()
              .includes(searchData.parsedSearchTerms[0]!.toLowerCase());
          }
        },
      },
      excelExportOptions: {
        valueParserCallback: (dataObj: unknown, { dataContext }: { dataContext: TRow }) =>
          (data._valueGetter ? data._valueGetter(dataContext) : "") || "",
      },
      grouping: {
        getter: (args: TRow) => (data._valueGetter ? data._valueGetter(args) : "") || "",
        collapsed: true,
        formatter(g: { rows: TRow[] }): string {
          // A per-column id → values dictionary. It used to be written as `new Set()` with the column
          // ids hung off it as properties: every access here and below is `visibleColumnsValues[id]`,
          // never a Set operation, and the outer Set's own contents stayed empty — so a plain object
          // is what the code has always treated it as. The inner per-column `Set`s are the real ones.
          const visibleColumnsValues: Record<string, Set<unknown>> = {};
          gridColumns(reactGridRef.current!).map((data: FilesGridColumn<TRow>) => {
            visibleColumnsValues[data.id] = new Set();
          });

          for (const obj of g.rows) {
            for (const column of gridColumns(reactGridRef.current!)) {
              if (column._valueGetter) {
                visibleColumnsValues[column.id]!.add(column._valueGetter(obj));
              }
            }
          }

          let html = "";
          gridColumns(reactGridRef.current!).map((data: FilesGridColumn<TRow>, index: number) => {
            if (visibleColumnsValues[data.id]!.size == 1 && Array.from(visibleColumnsValues[data.id]!)[0]) {
              html += `<span class='slick-cell l${index} r${index}'>${
                Array.from(visibleColumnsValues[data.id]!)[0]
              }</span>`;
            } else if (visibleColumnsValues[data.id]!.size > 1) {
              html += `<span class='slick-cell l${index} r${index} italic'>varies</span>`;
            }
          });
          return html;
        },
      },
      ...data,
    }),
  );
};

export const getFilesGridToolbar = ({
  searchTemplate,
  excelExportTemplate,
}: {
  searchTemplate: ToolbarItem["template"];
  excelExportTemplate: ToolbarItem["template"];
}): ToolbarItem[] => [
  {
    id: "excel_export",
    align: "Left" as const,
    template: excelExportTemplate,
  },
  {
    id: "search",
    align: "Right" as const,
    template: searchTemplate,
  },
];
